import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { guessCountry, loadCountry } from '../lib/geo';

const LanguageContext = createContext();

/** Langue imposée par une page pour tout l'onglet (/eat : anglais), sans toucher au choix mémorisé du site. */
const SESSION_KEY = 'openbro_lang_session';

function sessionLang() {
  try {
    const value = sessionStorage.getItem(SESSION_KEY);
    if (value === 'fr' || value === 'en') return value;
  } catch { /* stockage indisponible */ }
  return null;
}

function savedLang() {
  try {
    const saved = localStorage.getItem('openroom_lang');
    if (saved === 'fr' || saved === 'en') return saved;
  } catch { /* stockage indisponible */ }
  return null;
}

/** Langue d'une page qui ne suit pas la règle « Canada → français » (/eat) : choix de l'onglet, choix mémorisé, puis navigateur. */
export function preferredLang() {
  return sessionLang() || savedLang() || browserLang();
}

function browserLang() {
  const langs = typeof navigator !== 'undefined' ? [...(navigator.languages || []), navigator.language] : [];
  const first = String(langs.find(Boolean) || 'fr').toLowerCase();
  return first.startsWith('fr') ? 'fr' : 'en';
}

/**
 * Langue de départ : le choix déjà fait avec le bouton FR/EN ; sinon le français au Canada (deviné ici par le
 * fuseau, confirmé ensuite par le pays Cloudflare) ; sinon la langue du navigateur (fr* → FR, le reste → EN).
 */
function initialLang() {
  const forced = sessionLang();
  if (forced) return forced;
  const saved = savedLang();
  if (saved) return saved;
  if (guessCountry() === 'CA') return 'fr';
  return browserLang();
}

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(initialLang);

  // Pays réel (GET /api/geo, Cloudflare) : rattrape le fuseau quand il se trompe (VPN, réglage manuel).
  // Le choix fait au bouton FR/EN reste prioritaire.
  useEffect(() => {
    if (savedLang() || sessionLang()) return undefined;
    let alive = true;
    loadCountry().then((country) => {
      if (!alive || !country || savedLang() || sessionLang()) return;
      setLang(country === 'CA' ? 'fr' : browserLang());
    });
    return () => { alive = false; };
  }, []);

  const toggleLang = useCallback(() => {
    setLang(prev => {
      const next = prev === 'fr' ? 'en' : 'fr';
      localStorage.setItem('openroom_lang', next);
      return next;
    });
  }, []);

  const setLanguage = useCallback((l) => {
    setLang(l);
    localStorage.setItem('openroom_lang', l);
  }, []);

  const forceSessionLang = useCallback((l) => {
    try { sessionStorage.setItem(SESSION_KEY, l); } catch { /* stockage indisponible */ }
    setLang(l);
  }, []);

  return (
    <LanguageContext.Provider value={{ lang, toggleLang, setLanguage, forceSessionLang, isFr: lang === 'fr' }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => useContext(LanguageContext);
